import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon } from '@radix-ui/react-icons';
import { Box, Button, Card, Flex, Spinner,Text } from '@radix-ui/themes';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import TextFieldComponent from '../../components/inputs/Textfield/TextfieldComponent';
import { useAppDispatch } from '../../hooks/hooks';
import { useGetDepartamentsQuery } from '../../hooks/useGetDepartamentsQuery';
import { useGetFacultiesQuery } from '../../hooks/useGetFacultiesQuery';
import { resetUpdateCourseData } from '../../redux/slices/school/course';
import { RootState } from '../../redux/store';
import { routes } from '../../routes';
import { updateCourse } from '../../services/school/course/updateCourse';
import { UpdateCourseType } from '../../types/Course';
import { UpdateCourseFormValidation } from './UpdateCourseFormValidation';
import RadioGroupComponent from '../../components/inputs/RadioGroup/RadioGroupComponent';
import { dataURLtoFile, getFile, getFileExt } from '../../utils/fileUtils';
import { isEmpty } from 'lodash';
import { DragAndDropFileUpload } from '../../components/inputs/FileUpload/DragAndDropFileUpload';
import { useGetKnowledgeDomainsQuery } from '../../hooks/useGetKnowledgeDomainsQuery';
import { useGetKnowledgeSubDomainsQuery } from '../../hooks/useGetKnowledgeSubDomainsQuery';
import { useGetUniversitiesQuery } from '../../hooks/useGetUniversitiesQuery';
import CheckboxComponent from '../../components/inputs/Checkbox/CheckboxComponent';

export default function UpdateCourse() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const mutation = useMutation({ mutationFn: updateCourse, mutationKey: "updateCourse" })
  const updateCourseFormData = useSelector((state: RootState) => state.course);
  const queryClient = useQueryClient();
  let defaultValues = {...updateCourseFormData}
  if(!isEmpty(updateCourseFormData.docBase64)){
    const file = dataURLtoFile(updateCourseFormData.docBase64, getFileExt(updateCourseFormData.docBase64,"plan.pdf"));
    defaultValues = {...updateCourseFormData, file}
  }
  const { control, handleSubmit, setValue, formState: { errors }, reset, getValues,resetField,watch } = useForm<UpdateCourseType>({
    resolver: yupResolver(UpdateCourseFormValidation),
    defaultValues: defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  })

  const { data:universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });
  const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties,refetch:refetchFaculties } = useGetFacultiesQuery({ PageNumber: 1, PageSize: 400,IdUniversity:getValues('idUniversity') });
  const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments } = useGetDepartamentsQuery(
    { PageNumber: 1, PageSize: 400, IdFaculty: getValues('idFaculty')}, 
    { enabled: !!getValues('idFaculty')}
  );
  const { data: domains, isLoading: isLoadingDomains, isError: errorDomains } = useGetKnowledgeDomainsQuery({ PageNumber: 1, PageSize: 400 });
  const { data: subDomains, isLoading: isLoadingSubDomains, isError: errorSubDomains, refetch: refreshSubDomains } = useGetKnowledgeSubDomainsQuery(
    { PageNumber: 1, PageSize: 400, DomainId: getValues('idKnowledgeDomain') },
    { enabled: !!getValues('idKnowledgeDomain') }
  );
  const onSubmit = handleSubmit(async (data) => {
    try {
      if(data?.file){
        const { base64} =  await getFile(data.file)
        data.docBase64 = base64;
      }else{
        data.docBase64 = '';
      }
      const result = await mutation.mutateAsync(data);
      if (result) {
        toast.success('Curso atualizado com sucesso!');
        queryClient.invalidateQueries('courses-pagination');
        navigate(routes.courses);
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });
  const handleBack = (e: any) => {
    dispatch(resetUpdateCourseData());
    navigate(routes.courses);
  }
  const handleFacultyChange = (idFaculty: string) => {
    setValue('idFaculty', idFaculty);
    setValue('idDepartament', idFaculty);
    refreshDepartaments();
  };
  const handleKnowledgeDomainChange = (id: string) => {
    setValue('idKnowledgeDomain', id);
    if(isEmpty(id)){
      resetField('idKnowledgeSubDomain')
      return;
    }
    refreshSubDomains();
  };
  const watchTypeDuration = watch('typeDuration')
  const yearsEnabled = watchTypeDuration==='Years';
  const monthsEnabled = watchTypeDuration==='Months';
  const daysEnabled = watchTypeDuration==='Days';
  const handleCourseNameChange = (e: any) => {
    const name = e.target.value;
    setValue("name", name);
    setValue("description", name);
}

const handleOnChangeTypeDuration = (e: any) => {
  const typeDuraction  = e.target.value;
  setValue('typeDuration',typeDuraction);
  switch(typeDuraction){
    case 'Years': {
      resetField('day')
      resetField('month')
    }  break;
    case 'Months': {
      resetField('day')
      resetField('duration') //years
    }  break;
    case 'Days': {
      resetField('month')
      resetField('duration') //years
    }  break;
  }

};
const handleUniversityChange = (id: string) => {
  setValue('idUniversity', id);
  setValue('idFaculty', '');
  setValue('idDepartament', '');
  refetchFaculties();
};
  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size='1' >
        <Flex direction='column' gap='4'  style={{ overflow :'auto'}}>
        <Flex gap="8">
            <TextFieldComponent name="name" label="Nome" control={control} errors={errors} fieldProps={{ placeholder: 'Nome do Curso', onChange:handleCourseNameChange }} style={{ width: '100%' }} />
          </Flex>
          <Flex gap='4'>
            <TextFieldComponent name="code" label="Código do curso" control={control} errors={errors} fieldProps={{ placeholder: 'Código do curso' }} style={{ width: '100%' }} />
          </Flex>
          <Flex gap='4'>
             <CheckboxComponent name="isPreAccredited" label="Acreditado? " control={control} errors={errors} required onChange={(checked) => console.log("Checkbox está:", checked)} />
          </Flex>
          <Flex gap="8">
            <RadioGroupComponent name="typeDuration" label="Duração do curso/programa" control={control} errors={errors} style={{ width: '100%' }}
              options={[
                { value: "Days", label: "Dias"},
                { value: "Months", label: "Meses"},
                { value: "Years", label: "Anos"}
              ]}
              fieldProps={{
                style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' },
                onChange:handleOnChangeTypeDuration
              }}
            />
          </Flex>
          <Flex gap='4'>
            <TextFieldComponent name="day" label="Duração do curso (Dias)" control={control} errors={errors} fieldProps={{ placeholder: 'Duração do curso (Dias)', type: 'number',disabled:!daysEnabled  }} style={{ width: '100%' }} />
            <TextFieldComponent name="month" label="Duração do curso (Meses)" control={control} errors={errors} fieldProps={{ placeholder: 'Duração do curso (Meses)', type: 'number',disabled:!monthsEnabled }} style={{ width: '100%' }} />
            <TextFieldComponent name="duration" label="Duração do curso (Anos)" control={control} errors={errors} fieldProps={{ placeholder: 'Duração do curso (Anos)', type: 'number',disabled:!yearsEnabled }} style={{ width: '100%' }} />
          </Flex>
          <Flex gap='4'>
            <SelectComponent
              name="idKnowledgeDomain"
              label="Domínio de Conhecimento"
              control={control}
              errors={errors}
              style={{ width: '100%' }}
              options={domains?.items.map(domain => ({
                value: domain.id ?? "",
                label: domain.description ?? ""
              }))}
              itemsLoading={isLoadingDomains}
              itemsError={errorDomains}
              onSelect={handleKnowledgeDomainChange}
              required={true}

            />
            <SelectComponent
              name="idKnowledgeSubDomain"
              label="SubDomínio de Conhecimento"
              control={control}
              errors={errors}
              style={{ width: '100%' }}
              options={subDomains?.items.map(domain => ({
                value: domain.id ?? "",
                label: domain.description ?? ""
              }))}
              itemsLoading={isLoadingSubDomains}
              itemsError={errorSubDomains}
              required={true}
            />
          </Flex>
          <SelectComponent
            name="idUniversity"
            label="Instituição de Ensino Superior"
            control={control}
            errors={errors}
            style={{ width: '100%' }}
            options={universities?.items.map(u => ({
              value: u.id ?? "",
              label: u.description ?? ""
            }))}
            itemsLoading={isLoadingUniversities}
            itemsError={errorUniversities}
            onSelect={handleUniversityChange}
            required={true}
          />
          <SelectComponent name="idFaculty" label="Unidade orgânica" control={control} errors={errors} style={{ width: '100%' }}
            options={faculties?.items.map(faculty => ({
              value: faculty.id ?? "",
              label: faculty.description ?? ""
            }))}
            onSelect={handleFacultyChange}
          />

          <SelectComponent name="idDepartament" label="Departamento" control={control} errors={errors} style={{ width: '100%' }}
            options={departaments?.items.map(departament => ({
              value: departament.id ?? "",
              label: departament.description ?? ""
            }))}
          />
        <Flex gap="8">
            <RadioGroupComponent name="level" label="Nível" control={control} errors={errors} style={{ width: '100%' }}
              options={[
                { value: "Licenciatura", label: "Licenciatura" }
                , { value: "Mestrado", label: "Mestrado" }
                , { value: "Doutorado", label: "Doutorado" }
              ]}
              fieldProps={{
                style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
              }}
            />
          </Flex>
          <Flex gap="8">
            <RadioGroupComponent name="modality" label="Modalidade" control={control} errors={errors} style={{ width: '100%' }}
              options={[
                { value: "1", label: "Presencial"},
                { value: "2", label: "Distância"},
              ]}
              fieldProps={{
                style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
              }}
            />
          </Flex>
          
          <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
            <Button type='button' variant='soft' color='gray' onClick={handleBack} disabled={mutation.isLoading}>
              <ChevronLeftIcon /> Voltar
            </Button>
            <Button type="submit" disabled={mutation.isLoading} variant='surface'>
              {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
              Actualizar
            </Button>
          </Flex>
        </Flex>
      </Card>
    </form>
  );
}